/**
 * Cross-tab refresh coordination (DNKADM-0497 — the front desk stays signed in).
 *
 * saas-auth rotates the refresh cookie on every `POST /api/auth/refresh`. Two tabs
 * (or a waking iPad) refreshing at once used to present the same cookie twice; the
 * loser was signed out. saas-auth (SASATH-0400) now answers that loser with
 * `401 REFRESH_TOKEN_SUPERSEDED` and revokes nothing. This module makes the app
 * side cooperate:
 *
 *  1. Every refresh runs inside the Web Lock `dink-admin-refresh`, so tabs refresh
 *     one at a time. Inside the lock, if ANOTHER tab of the same signed-in user
 *     refreshed in the last few seconds (announced over a same-origin
 *     `BroadcastChannel`), that result is reused instead of refreshing again.
 *     With no `navigator.locks` (Safari < 15.4, insecure context) there is no lock
 *     and no reuse: the refresh goes straight out, as before. Inside the lock the
 *     refresh is rebuilt before it is sent, so it carries the CSRF token that
 *     matches the cookie as it is NOW (each refresh rotates `journix_csrf`), and
 *     with Web Locks every CSRF read for a refresh happens inside the lock. The
 *     lock wait is bounded (`REFRESH_LOCK_WAIT_MS`); past it the refresh goes out
 *     unlocked rather than waiting on a holder that never finishes. The lock HOLD
 *     is bounded too (`REFRESH_LOCK_HOLD_MS`): a slow refresh is never aborted, but
 *     it releases the lock after the bound and its result is still used.
 *  2. A `401 REFRESH_TOKEN_SUPERSEDED` is never a sign-out: the refresh is sent
 *     once more (the cookie now holds the newer token). DNKADM-0501: if that
 *     retry is SUPERSEDED too, the refresh waits — for another tab's announced
 *     result, for the lock holder (when this refresh is not inside the lock), or
 *     `REFRESH_SUPERSEDED_WAIT_MS` (1.5 s), whichever comes first, never more than
 *     `REFRESH_SUPERSEDED_MAX_WAIT_MS` (3 s) — and then tries ONCE more, built
 *     fresh: a reload can cancel a refresh after saas-auth rotated the token, and
 *     that request's (keepalive) answer may still land the new cookie meanwhile.
 *     A sign-out before or during the wait ends it with no further attempt. A
 *     failure of the last try, or any other error, is returned unchanged, so the
 *     caller signs out as it always did.
 *
 * Tokens stay IN MEMORY ONLY. The broadcast carries the refresh result between
 * live same-origin tabs; nothing is written to localStorage, sessionStorage or a
 * cookie.
 *
 * This works at the axios ADAPTER level (`client.ts` installs it on the
 * `/api/auth/refresh` POST), so every refresh path — the vendored factory's
 * `runRefresh` and `bootRefresh` (AuthGate / AuthReturn cold boot) — goes through
 * it without editing the vendored `httpClientFactory.ts`. At adapter level the
 * response body is still the raw transport payload (axios parses JSON after the
 * adapter), so bodies are parsed defensively here.
 */
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export const REFRESH_LOCK_NAME = 'dink-admin-refresh';
export const REFRESH_CHANNEL_NAME = 'dink-admin-refresh';
export const REFRESH_SUPERSEDED_CODE = 'REFRESH_TOKEN_SUPERSEDED';
/** "Another tab refreshed in the last few seconds." */
export const REFRESH_REUSE_WINDOW_MS = 5_000;
/**
 * Longest a refresh waits for the lock. A holder that never finishes (a frozen
 * background tab, a half-up network) must not stall every tab: after this the
 * refresh goes out unlocked, as it did before the lock existed. Never a sign-out.
 */
export const REFRESH_LOCK_WAIT_MS = 10_000;
/**
 * Longest a refresh HOLDS the lock. A slow refresh is never aborted (today it
 * eventually succeeds; cutting it off would be a sign-out). Past this the lock is
 * released so other tabs proceed — their SUPERSEDED retry covers any overlap —
 * while this refresh keeps running and its result is still used.
 */
export const REFRESH_LOCK_HOLD_MS = 10_000;
/**
 * DNKADM-0501 — once the SUPERSEDED retry is SUPERSEDED as well, how long to wait
 * for the lock holder or another tab's announced result before the one last try.
 */
export const REFRESH_SUPERSEDED_WAIT_MS = 1_500;
/** DNKADM-0501 — hard ceiling on that extra wait, whatever bound is configured. */
export const REFRESH_SUPERSEDED_MAX_WAIT_MS = 3_000;

const MESSAGE_TYPE = 'dink-admin-refreshed';

export interface RefreshResult {
  accessToken: string;
  expiresAt: string | null;
  csrfToken: string | null;
}

/** The slice of `navigator.locks` used here. */
export interface RefreshLocks {
  /** Rejects (AbortError) if `signal` aborts before the lock is granted. */
  request<T>(name: string, callback: () => Promise<T>, signal?: AbortSignal): Promise<T>;
}

/** The slice of `BroadcastChannel` used here. */
export interface RefreshChannel {
  postMessage(message: unknown): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export interface RefreshCoordinatorDeps {
  /** Resolved per refresh; `null` = no Web Locks, today's unserialized behavior. */
  locks: () => RefreshLocks | null;
  /** Created lazily, only when locks exist; `null` = no reuse, lock only. */
  channel: () => RefreshChannel | null;
  /** Whether another tab's result belongs to this tab's signed-in user. */
  canReuse: (result: RefreshResult) => boolean;
  /** Lock-wait bound; default `REFRESH_LOCK_WAIT_MS`. */
  lockWaitMs?: () => number;
  /** Lock-hold bound; default `REFRESH_LOCK_HOLD_MS`. */
  lockHoldMs?: () => number;
  /**
   * SUPERSEDED recovery wait (DNKADM-0501); default `REFRESH_SUPERSEDED_WAIT_MS`,
   * never above `REFRESH_SUPERSEDED_MAX_WAIT_MS`.
   */
  supersededWaitMs?: () => number;
  now?: () => number;
}

export interface RefreshRunOptions {
  /**
   * True once this refresh's result can no longer be used (DNKADM-0501: the user
   * signed out after it started). Checked before and after the SUPERSEDED recovery
   * wait, so a sign-out ends the recovery with no further attempt.
   */
  cancelled?: () => boolean;
}

/** How one `run` attempts the refresh (DNKADM-0501 adds where the recovery wait looks for a lock holder). */
interface AttemptMode {
  /** Inside the lock: announce results and reuse another tab's. */
  withReuse: boolean;
  locks: RefreshLocks | null;
  /** This refresh holds the lock (so there is no other holder to wait for). */
  insideLock: boolean;
  cancelled: () => boolean;
}

/** Sends one refresh. Resolves on 2xx; rejects like an axios adapter (an error with `response`) otherwise. */
export type RefreshSend = () => Promise<AxiosResponse>;

export interface RefreshCoordinator {
  /**
   * Run one refresh for `config`.
   *
   * `send` sends the request exactly as axios built it (the real transport adapter).
   * It is used only on the no-lock path, which is today's behavior.
   *
   * `sendFresh` builds and sends a NEW refresh request, so its CSRF token is read
   * at send time. saas-auth's refresh is CSRF double-submit protected and every
   * refresh rotates the `journix_csrf` cookie, so a request built before waiting
   * on the lock (or before a SUPERSEDED answer) carries a token that may no longer
   * match the cookie. Inside the lock, the first attempt and the single SUPERSEDED
   * retry therefore both use `sendFresh`. The DNKADM-0501 last try after the
   * recovery wait always uses `sendFresh` too.
   */
  run(
    config: InternalAxiosRequestConfig,
    send: RefreshSend,
    sendFresh: RefreshSend,
    options?: RefreshRunOptions,
  ): Promise<AxiosResponse>;
  /** Forget any other tab's result (on sign-out). */
  clear(): void;
}

export function createRefreshCoordinator(deps: RefreshCoordinatorDeps): RefreshCoordinator {
  const now = deps.now ?? (() => Date.now());
  let fromOtherTab: { result: RefreshResult; at: number } | null = null;
  let channel: RefreshChannel | null | undefined;
  /** Woken when another tab announces a result (ends a DNKADM-0501 recovery wait early). */
  const announceWaiters = new Set<() => void>();

  function getChannel(): RefreshChannel | null {
    if (channel !== undefined) return channel;
    channel = deps.channel();
    if (channel) {
      channel.onmessage = (event) => {
        const data = event.data as { type?: unknown; result?: unknown } | null;
        if (!data || data.type !== MESSAGE_TYPE) return;
        const result = toResult(data.result);
        if (!result) return;
        fromOtherTab = { result, at: now() };
        for (const wake of [...announceWaiters]) wake();
      };
    }
    return channel;
  }

  function reusable(): RefreshResult | null {
    if (!fromOtherTab) return null;
    if (now() - fromOtherTab.at > REFRESH_REUSE_WINDOW_MS) return null;
    return deps.canReuse(fromOtherTab.result) ? fromOtherTab.result : null;
  }

  function announce(res: AxiosResponse): void {
    const result = toResult(parseBody(res.data));
    if (!result) return;
    try {
      getChannel()?.postMessage({ type: MESSAGE_TYPE, result });
    } catch {
      /* a closed channel only loses the reuse optimisation */
    }
  }

  /**
   * DNKADM-0501 recovery wait: resolves when another tab announces a result, when
   * the lock holder releases (checked only when this refresh is NOT inside the
   * lock — inside it, this tab is the holder), or after the bound. Never rejects.
   */
  function waitForRecovery(locks: RefreshLocks | null, insideLock: boolean): Promise<void> {
    const configured = deps.supersededWaitMs?.() ?? REFRESH_SUPERSEDED_WAIT_MS;
    const ms = Math.min(Math.max(0, configured), REFRESH_SUPERSEDED_MAX_WAIT_MS);
    return new Promise<void>((resolve) => {
      const controller = new AbortController();
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        announceWaiters.delete(finish);
        controller.abort();
        resolve();
      };
      const timer = setTimeout(finish, ms);
      announceWaiters.add(finish);
      if (locks && !insideLock) {
        // Granted = the holder finished. Released at once; aborted if the wait ends first.
        locks
          .request(
            REFRESH_LOCK_NAME,
            async () => {
              finish();
            },
            controller.signal,
          )
          .catch(() => undefined);
      }
    });
  }

  async function attempt(
    config: InternalAxiosRequestConfig,
    send: RefreshSend,
    retry: RefreshSend,
    mode: AttemptMode,
  ): Promise<AxiosResponse> {
    const { withReuse } = mode;
    try {
      const res = await send();
      if (withReuse) announce(res);
      return res;
    } catch (err) {
      if (!isSuperseded(err)) throw err;
      // Another tab won the rotation a moment ago. Prefer its announced result;
      // otherwise retry ONCE — the cookie now holds the newer token.
      const shared = withReuse ? reusable() : null;
      if (shared) return synthesize(config, shared);
      try {
        const res = await retry();
        if (withReuse) announce(res);
        return res;
      } catch (retryErr) {
        if (!isSuperseded(retryErr)) throw retryErr;
        // DNKADM-0501: still SUPERSEDED. A refresh cancelled by a reload may be
        // landing the newer cookie right now — wait (bounded) for it, the lock
        // holder or another tab's result, then try ONCE more, built fresh. A
        // sign-out before or during the wait ends it here, with no further
        // attempt. A failure of the last try propagates unchanged and the caller
        // signs out as before.
        if (mode.cancelled()) throw retryErr;
        await waitForRecovery(mode.locks, mode.insideLock);
        if (mode.cancelled()) throw retryErr;
        const late = withReuse ? reusable() : null;
        if (late) return synthesize(config, late);
        const res = await retry();
        if (withReuse) announce(res);
        return res;
      }
    }
  }

  return {
    async run(config, send, sendFresh, options) {
      const cancelled = options?.cancelled ?? (() => false);
      const locks = deps.locks();
      if (!locks) {
        return attempt(config, send, sendFresh, { withReuse: false, locks: null, insideLock: false, cancelled });
      }
      getChannel(); // start listening before the first refresh
      // Bounded wait (manual timer + AbortController: AbortSignal.timeout needs
      // Safari 16, Web Locks only 15.4).
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), deps.lockWaitMs?.() ?? REFRESH_LOCK_WAIT_MS);
      let work: Promise<AxiosResponse> | null = null;
      try {
        await locks.request(
          REFRESH_LOCK_NAME,
          async () => {
            clearTimeout(timer);
            const shared = reusable();
            // Built fresh: another refresh may have rotated the CSRF cookie while this one waited.
            work = shared
              ? Promise.resolve(synthesize(config, shared))
              : attempt(config, sendFresh, sendFresh, { withReuse: true, locks, insideLock: true, cancelled });
            // Hold the lock until the refresh settles, or at most the hold bound.
            await settledOrAfter(work, deps.lockHoldMs?.() ?? REFRESH_LOCK_HOLD_MS);
          },
          controller.signal,
        );
      } catch {
        // Not granted in time (or the lock API failed) — `work` never started.
      } finally {
        clearTimeout(timer);
      }
      if (work) return work;
      // The lock was not granted in time: go out unlocked, as before the lock
      // existed — still built fresh, since with locks present the pre-built request
      // carries no CSRF token.
      return attempt(config, sendFresh, sendFresh, { withReuse: false, locks, insideLock: false, cancelled });
    },
    clear() {
      fromOtherTab = null;
    },
  };
}

/** Resolve when `promise` settles or after `ms`, whichever comes first. Never rejects. */
async function settledOrAfter(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      promise.then(
        () => undefined,
        () => undefined,
      ),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** True for a `401` whose saas-auth envelope (`{ error: { code } }`) says REFRESH_TOKEN_SUPERSEDED. */
export function isSuperseded(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response || response.status !== 401) return false;
  const body = parseBody(response.data) as { error?: { code?: unknown } } | null;
  return body?.error?.code === REFRESH_SUPERSEDED_CODE;
}

/** Browser Web Locks, or `null` where the API is missing (today's behavior). */
export function browserRefreshLocks(): RefreshLocks | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as { locks?: { request?: unknown } }).locks;
  if (!locks || typeof locks.request !== 'function') return null;
  const manager = locks as unknown as LockManager;
  return {
    request: <T>(name: string, callback: () => Promise<T>, signal?: AbortSignal) =>
      manager.request(
        name,
        signal ? { mode: 'exclusive', signal } : { mode: 'exclusive' },
        callback,
      ) as Promise<T>,
  };
}

/** A same-origin BroadcastChannel, or `null` where the API is missing. */
export function browserRefreshChannel(): RefreshChannel | null {
  if (typeof BroadcastChannel !== 'function') return null;
  try {
    const ch = new BroadcastChannel(REFRESH_CHANNEL_NAME);
    // Node's BroadcastChannel (test runs) would otherwise hold the process open.
    (ch as unknown as { unref?: () => void }).unref?.();
    return ch as unknown as RefreshChannel;
  } catch {
    return null;
  }
}

function synthesize(config: InternalAxiosRequestConfig, result: RefreshResult): AxiosResponse {
  return {
    data: { accessToken: result.accessToken, expiresAt: result.expiresAt, csrfToken: result.csrfToken },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  };
}

function parseBody(data: unknown): unknown {
  if (typeof data !== 'string') return data ?? null;
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
}

function toResult(value: unknown): RefreshResult | null {
  const v = value as { accessToken?: unknown; expiresAt?: unknown; csrfToken?: unknown } | null;
  if (!v || typeof v.accessToken !== 'string' || v.accessToken.length === 0) return null;
  return {
    accessToken: v.accessToken,
    expiresAt: typeof v.expiresAt === 'string' ? v.expiresAt : null,
    csrfToken: typeof v.csrfToken === 'string' ? v.csrfToken : null,
  };
}
